import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import { of, switchMap } from 'rxjs';
import { AuthService } from '../../../services/auth.service';

export const PluginRoleGuard: CanActivateFn | CanActivateChildFn = (
    route,
    state
) => {
    const router: Router = inject(Router);
    const authService = inject(AuthService);

    // Check the authentication status first
    return authService.isUserLoggedIn().pipe(
        switchMap((authenticated) => {
            // If the user is not authenticated...
            if (!authenticated) {
                const redirectURL =
                    state.url === '/sign-out' ? '' : `redirectURL=${state.url}`;
                const urlTree = router.parseUrl(`sign-in?${redirectURL}`);
                return of(urlTree);
            }

            // Check for required plugins (user must have ALL plugins)
            const requiredPlugins: string[] = route.data?.['plugins'] || [];
            if (requiredPlugins.length > 0) {
                const hasAllPlugins = requiredPlugins.every((plugin) =>
                    authService.userHasPlugin(plugin)
                );

                if (!hasAllPlugins) {
                    // Redirect to unauthorized page if plugins check fails
                    const urlTree = router.parseUrl('unauthorized-access');
                    return of(urlTree);
                }
            }

            // Check for required roles (user must have at least ONE role)
            const requiredRoles: string[] = route.data?.['roles'] || [];
            if (requiredRoles.length > 0) {
                const hasRequiredRole = requiredRoles.some((role) =>
                    authService.userHasRole(role)
                );

                if (!hasRequiredRole) {
                    // Redirect to unauthorized page if roles check fails
                    const urlTree = router.parseUrl('unauthorized-access');
                    return of(urlTree);
                }
            }

            // Check for route permissions (existing functionality)
            const requiredPermissions: string[] =
                route.data?.['permissions'] || [];
            if (requiredPermissions.length > 0) {
                const hasAllPermissions = requiredPermissions.every(
                    (permission) => authService.userHasPermission(permission)
                );

                if (!hasAllPermissions) {
                    // Redirect to unauthorized page if permissions fail
                    const urlTree = router.parseUrl('unauthorized-access');
                    return of(urlTree);
                }
            }

            // Allow the access
            return of(true);
        })
    );
};
